import { getTeacherByEmail } from '../slices/userSlice/authSlice'
import { toast } from 'react-toastify'
import axios from 'axios'
import { deviceDetect } from 'react-device-detect'

const publicIp = require('react-public-ip')

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

const getErrorMessage = (error, fallback = 'Une erreur est survenue') => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data ||
    error?.message ||
    fallback
  )
}

// Login User
export function loginUser(user) {
  return async (dispatch) => {
    try {
      let public_ipv4 = ''

      try {
        public_ipv4 = (await publicIp.v4()) || ''
      } catch (ipErr) {
        console.warn("Impossible de récupérer l'IP publique", ipErr)
      }

      const deviceInfo = deviceDetect()

      const { data, status } = await api.post('/teachers/auth', {
        ...user,
        public_ipv4,
        deviceInfo,
      })

      if (status === 200) {
        localStorage.setItem('teacherInfo', JSON.stringify(data))
        dispatch(getTeacherByEmail({ MAIL_PRO_ENS: user?.username }))
        toast.success('Connexion réussie')
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur de connexion au serveur')
      console.error('Login Error:', message)
      toast.error(message)
      localStorage.removeItem('teacherInfo')
    }
  }
}

// Forgot Password
export function forgotPassword({ user, recaptchaToken, lang }) {
  return async () => {
    try {
      await api.post('/teachers/forgot-password', {
        user,
        recaptchaToken,
      })

      toast.success(
        lang === 'Français'
          ? 'Veuillez consulter votre mail pour réinitialiser votre mot de passe'
          : 'Please check your email to reset your password',
      )
    } catch (error) {
      const status = error?.response?.status

      switch (status) {
        case 401:
          toast.error(getErrorMessage(error, 'Unauthorized'))
          break
        case 400:
          toast.error(
            lang === 'Français'
              ? "Erreur ! Il n'y a aucun enseignant avec cet email"
              : 'Error! There are no teachers with this email',
          )
          break
        case 404:
          toast.error(
            lang === 'Français'
              ? "Erreur ! L'email n'a pas pu être envoyé"
              : 'Error! The email could not be sent',
          )
          break
        default:
          toast.error(
            getErrorMessage(
              error,
              lang === 'Français'
                ? 'Erreur lors de la demande de réinitialisation'
                : 'Password reset request failed',
            ),
          )
      }
    }
  }
}

// Request account
export function requestAccount({ user, recaptchaToken, lang }) {
  return async () => {
    try {
      await api.post('/teachers/request-account', {
        user,
        recaptchaToken,
      })

      toast.success(
        lang === 'Français'
          ? 'Veuillez consulter votre mail pour activer votre compte'
          : 'Please check your email to activate your account',
      )
    } catch (error) {
      const status = error?.response?.status

      switch (status) {
        case 401:
          toast.error(getErrorMessage(error, 'Unauthorized'))
          break
        case 400:
          toast.error(
            lang === 'Français'
              ? "Erreur ! Il n'y a aucun enseignant avec cet email"
              : 'Error! There are no teachers with this email',
          )
          break
        case 404:
          toast.error(
            lang === 'Français'
              ? "Erreur ! L'email n'a pas pu être envoyé"
              : 'Error! The email could not be sent',
          )
          break
        case 405:
          toast.error(
            lang === 'Français'
              ? 'Votre compte est déjà activé. Réinitialisez votre mot de passe !'
              : 'Your account is already activated. Reset your password!',
          )
          break
        default:
          toast.error(
            getErrorMessage(
              error,
              lang === 'Français'
                ? 'Erreur lors de la demande d’activation'
                : 'Account activation request failed',
            ),
          )
      }
    }
  }
}

// Reset Password
export function resetPassword({ MAIL_PRO_ENS, password, token, recaptchaToken, lang }) {
  return async () => {
    try {
      const res = await api.post('/teachers/reset-password', {
        MAIL_PRO_ENS,
        password,
        token,
        recaptchaToken,
      })

      toast.success(
        lang === 'Français'
          ? 'Votre mot de passe a été réinitialisé avec succès'
          : 'Your password has been successfully reset',
      )

      return res.status
    } catch (error) {
      const status = error?.response?.status

      switch (status) {
        case 401:
        case 403:
          toast.error(
            lang === 'Français'
              ? 'Erreur de réinitialisation de mot de passe'
              : 'Password reset error',
          )
          break
        case 400:
          toast.error(
            lang === 'Français'
              ? "Erreur ! Il n'y a aucun enseignant avec cet email"
              : 'Error! There are no teachers with this email',
          )
          break
        default:
          toast.error(
            getErrorMessage(
              error,
              lang === 'Français' ? 'Erreur de réinitialisation' : 'Password reset failed',
            ),
          )
      }

      return status
    }
  }
}

// Activate Account
export function activateAccount({ MAIL_PRO_ENS, password, token, recaptchaToken, lang }) {
  return async () => {
    try {
      const res = await api.post('/teachers/activate-account', {
        MAIL_PRO_ENS,
        password,
        token,
        recaptchaToken,
      })

      toast.success(
        lang === 'Français'
          ? 'Votre compte a été activé avec succès'
          : 'Your account has been successfully activated',
      )

      return res.status
    } catch (error) {
      const status = error?.response?.status

      switch (status) {
        case 401:
        case 403:
          toast.error(
            lang === 'Français'
              ? "Erreur d'activation de votre compte"
              : 'Error activating your account',
          )
          break
        case 400:
          toast.error(
            lang === 'Français'
              ? "Erreur ! Il n'y a aucun enseignant avec cet email"
              : 'Error! There are no teachers with this email',
          )
          break
        default:
          toast.error(
            getErrorMessage(
              error,
              lang === 'Français' ? "Erreur d'activation" : 'Account activation failed',
            ),
          )
      }

      return status
    }
  }
}

// Logout User
export function logoutUser() {
  return async () => {
    try {
      localStorage.removeItem('teacherInfo')
    } catch (error) {
      toast.error(getErrorMessage(error, 'Logout failed'))
    }
  }
}
